import type {
    DetailViewContext,
    ControlFactoryTuple,
    ControlFactory,
} from 'radpresentation.htmlclient.defaultviewextensions';
import {
    UIListener,
    ComponentManager,
    Loader,
    Utilities,
    MetadataGroupFactory,
} from 'radpresentation.htmlclient.defaultviewextensions';
import type ExtensionEditorViewModel from '@/ViewModels/Controls/ExtensionEditorViewModel';
const PDTypeMeta = 'PDTypeMeta';
const ExtensionEditor = 'ExtensionEditorJSON';

class ExtensionManagerUIListener extends UIListener {
    constructor() {
        super();
    }

    /**
     * @override
     * @param context
     */
    public onBeforeRender(context: DetailViewContext): void {
        if (!this.isDetailViewContext(context)) {
            return;
        }

        MetadataGroupFactory.hideChangelogsAndShowMetadata(context);

        let detailViewGroups = context.detailViewContextDTO.detailView
            .detailViewGroups as Array<{
            ergName: string;
            items: Array<{ ergName: string; uiGenerator: string }>;
        }>;
        let groupIndex = _.findIndex(
            detailViewGroups,
            group =>
                group.ergName ===
                'RADEM.RADExtensionManager.ExtensionManager_DVG_DEFAULT'
        );
        if (groupIndex === -1) {
            return;
        }

        let detailViewGroup = detailViewGroups[groupIndex];
        let items = detailViewGroup.items;
        let itemIndex = _.findIndex(
            items,
            item =>
                item.ergName ===
                'RADEM.RADEXTENSIONMANAGER.EXTENSIONMANAGER_EXTENSIONEDITOR_ERGNAME'
        );
        if (itemIndex === -1) {
            return;
        }

        let item = items[itemIndex];
        item.uiGenerator = 'EmptyElement';

        context.detailViewContextDTO.detailView.autoUpdateDerivedProperties = false;
    }

    public onBeforeCancel(context: DetailViewContext): boolean {
        const viewModel = this.getViewModel(context);
        if (viewModel.preventCancel()) return false;

        return true;
    }

    /**
     * @override
     * @param context
     */
    public onDetailViewReady(context: DetailViewContext): void {
        context.genericObject.onDelayedChange(() => {
            if (this.onDetailViewReady['doNotUpdate'] !== true) {
                Loader.getInstance().show();
                context.updateProperties().always(() => {
                    Loader.getInstance().hide();
                });
            }
            this.onDetailViewReady['doNotUpdate'] = false;
        }, context);

        if (
            context.genericObject.getValueReturnNullIfNotExists(
                ExtensionEditor
            ) != null
        ) {
            let viewModel = this.getViewModel(context);

            if (viewModel != null) {
                this.onDetailViewReady['doNotUpdate'] = true;
                context.genericObject.isModified(true);
                viewModel.updateViewModel(context.genericObject);
            }
        }
    }

    /**
     * @override
     * @param context
     */
    public onBeforeApply(context: DetailViewContext): boolean {
        let viewModel = this.getViewModel(context);
        let isValid = viewModel.validate();
        if (!isValid) {
            return false;
        }

        this.onDetailViewReady['doNotUpdate'] = true;

        this.syncPropertyGroupCollapsedStatesToLocalStorage(context);
        this.updateDtoString(context);

        return true;
    }

    private syncPropertyGroupCollapsedStatesToLocalStorage(
        context: DetailViewContext
    ): void {
        this.getViewModel(
            context
        ).syncPropertyGroupCollapsedStatesToLocalStorage();
    }

    private updateDtoString(context: DetailViewContext): void {
        this.getViewModel(context).updateDtoString();
    }

    /**
     * @override
     * @param context
     * @param isClosing
     */
    public onAfterApply(context: DetailViewContext, isClosing: boolean): void {
        if (!isClosing) {
            context.genericObject.isModified(true);
            context.forceUpdateProperties();
        }
    }

    /**
     * @override
     * @param context
     * @param updatedProperties
     */
    public onGenericObjectUpdated(
        context: DetailViewContext,
        updatedProperties: any
    ): void {
        if (_.contains(Object.keys(updatedProperties), ExtensionEditor)) {
            let viewModel = this.getViewModel(context);

            let json = JSON.parse(
                context.genericObject.values['ExtensionEditorJSON']()
            );

            if (
                !!this.onGenericObjectUpdated['lastJson'] ||
                this.onGenericObjectUpdated['lastJson'] !== json
            ) {
                this.onGenericObjectUpdated['lastJson'] = json;
                viewModel.updateViewModel(context.genericObject);
            }

            if (viewModel != null) {
                if (this.onGenericObjectUpdated['doHide'] === true) {
                    Loader.getInstance().hide();
                    this.onGenericObjectUpdated['doHide'] = false;
                }
            }
        }
    }

    private getPDTypeMeta(context): string {
        let filter = (factory: ControlFactory) =>
            factory.detailViewItem &&
            factory.detailViewItem.name == 'PDTypeMeta';
        let value = context
            .getCreatedFactories()
            .filter(filter)[0]
            ['viewModel'].textBox.value();

        return value;
    }

    /**
     * @override
     * @param context
     * @param relation
     * @param actionType
     * @param additionalParameters
     */
    public onBeforeAssociationUpdated(
        context: DetailViewContext,
        relation: string,
        actionType: string,
        additionalParameters: { toPdoIds: Array<string> }
    ): JQueryPromise<boolean> {
        let pdTypeMeta = this.getPDTypeMeta(context);

        this.onAssociationUpdated['pdTypeMetaBeforeAssociationUpdated'] =
            pdTypeMeta;

        return Utilities.resolveImmediately(true);
    }

    /**
     * entered immediately after a selection in the association-control has been made
     * @override
     * @param context
     * @param relation
     */
    public onAssociationUpdated(
        context: DetailViewContext,
        relation: string
    ): void {
        if (relation != PDTypeMeta) {
            return;
        }

        let pdTypeMeta = this.getPDTypeMeta(context);
        if (
            this.onAssociationUpdated['pdTypeMetaBeforeAssociationUpdated'] ==
            pdTypeMeta
        ) {
            return;
        }

        this.onGenericObjectUpdated['doHide'] = true;

        Loader.getInstance().show();

        context.genericObject.refreshProperties([ExtensionEditor]);
    }

    public getControlFactoryByIdentifier(
        context: DetailViewContext,
        identifier: string
    ): ControlFactory {
        return context.getUIFactory(identifier) as ControlFactory;
    }

    private getViewModel(context: DetailViewContext): ExtensionEditorViewModel {
        let item = context.getCreatedTuple(
            ExtensionEditor
        ) as ControlFactoryTuple;
        let factory = item.factory as ControlFactory;
        let viewModel = factory.viewModel as ExtensionEditorViewModel;

        return viewModel;
    }
}

export default ExtensionManagerUIListener;
ComponentManager.setUIListener(
    'RADExtensionManagerViewExtension.UIListeners.ExtensionManagerUIListener',
    ExtensionManagerUIListener,
    'RADExtensionManagerHTMLViewExtension'
);
ComponentManager.setUIListener(
    'ExtensionManagerUIListener',
    ExtensionManagerUIListener,
    'RADExtensionManagerHTMLViewExtension'
);
